import React, { useEffect, useRef, useState } from "react";
import { Headphones } from "lucide-react";
import { useStudio } from "@/studio/StudioContext";

// Routes the pre-fader deck cue bus to a chosen audio output (e.g. headphones / USB
// interface) so a DJ can pre-listen to one deck while the other plays on the master.
export default function HeadphoneCue() {
  const { engine } = useStudio();
  const audioRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("default");
  const [supported, setSupported] = useState(true);
  const [active, setActive] = useState(false);

  const refreshDevices = async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audiooutput"));
    } catch (_) {}
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (typeof el.setSinkId !== "function") setSupported(false);
    refreshDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", refreshDevices);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", refreshDevices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = async () => {
    const el = audioRef.current;
    engine.ensureStarted();
    if (engine.ctx?.state === "suspended") await engine.ctx.resume();
    try {
      el.srcObject = engine.cueStream;
      await el.play();
      // Ask for permission so device labels populate.
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      await refreshDevices();
      setActive(true);
    } catch (_) {}
  };

  const onSelect = async (id) => {
    setDeviceId(id);
    const el = audioRef.current;
    if (el && typeof el.setSinkId === "function" && id) {
      try {
        await el.setSinkId(id);
      } catch (_) {}
    }
  };

  return (
    <div
      className="rounded-md border border-[#262626] bg-[#0d0d0d] p-3 flex flex-col gap-2"
      data-testid="headphone-cue"
    >
      <div className="flex items-center gap-2">
        <Headphones size={15} className="text-[#00e5ff]" />
        <span className="hw-label">Headphone Cue — pre-listen a deck</span>
      </div>
      {!supported ? (
        <p className="text-[11px] text-neutral-500">
          Your browser can't send audio to a separate output. Use Chrome/Edge on desktop for headphone
          cueing. You can still tap CUE on a deck to route it to the cue bus.
        </p>
      ) : !active ? (
        <button
          data-testid="cue-enable-btn"
          onClick={enable}
          className="self-start flex items-center gap-2 px-3 py-2 rounded-sm border border-[#00e5ff] text-[#00e5ff] text-xs font-bold hover:bg-[#00e5ff]/10 transition-colors"
        >
          <Headphones size={14} /> Turn on headphone cue
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-neutral-400 shrink-0">Output</span>
            <select
              data-testid="cue-device-select"
              value={deviceId}
              onChange={(e) => onSelect(e.target.value)}
              className="flex-1 bg-[#050505] border border-[#262626] rounded-sm px-2 py-1.5 text-xs text-neutral-200 outline-none focus:border-cyan-500"
            >
              <option value="default">Default output</option>
              {devices.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>
                  {d.label || `Output ${i + 1}`}
                </option>
              ))}
            </select>
          </div>
          <p className="text-[10px] text-neutral-600">
            Pick your headphones or DJ interface, then tap <span className="text-[#00e5ff] font-bold">CUE</span> on a deck to
            hear it privately while the master keeps playing.
          </p>
        </div>
      )}
      <audio ref={audioRef} className="hidden" />
    </div>
  );
}
